// BLOCUL SOLUȚIEI, în capul meniului plutitor al vizualizatorului (Corecția
// lui Stefan, 25.09): numele, fazele, acțiunile disciplinei și «Schimbă ▾».
// Vizualizatorul nu mai are listă, coloană sau bară de soluții — planșa ia
// tot ecranul, iar schimbarea soluției se face din lanț sau de aici, prin
// același comutator.
//
// Acțiunile, pe disciplină:
//   * situație, arhitectură: ★ «Fă favorită»; la arhitectură, sub «⋯»,
//     încercările parcării, excavația și refacerea subsolului;
//   * structură: «Pornește calculul detaliat» (D2, D9), «Generează structura»
//     și «Importă structura» pe o schiță fără soluția de felul lor, «Editează
//     geometria macro» (A8), «Reia schița» când arhitectura s-a editat după
//     calcul, «Rerulează», ■.

import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { dataOra, useT } from "../i18n";
import FazeBara from "../components/FazeBara";
import { useRolProiect } from "../components/rol";
import { Stea } from "../components/ui";
import { useRegistru } from "./Registru";
import { useActiuni } from "./actiuni";
import type { SolutieRand } from "./api";
import Comutator from "./Comutator";
import VarianteEvacuare from "./VarianteEvacuare";
import MeniuLista from "./MeniuLista";
import {
  CORP_TOATE,
  areCirculatia, areStructuraDesenata, caleEtapa, candRand, eDeschis, elementulRandului,
  fazeleElementului, fazeleRandului,
  numeElement, numeFaza, numeRand, poateGeneraSolutia, poatePornireDetaliu, poateRefaceSubsolul,
} from "./selectie";
import "./nav.css";

export default function BlocSolutie({ rand, corp, laEditeaza, laPregatesteEditarea, extra,
                                      meniuArhitectura, laSubsol = false }: {
  rand: SolutieRand;
  /** codul corpului privit, la structură */
  corp?: string | null;
  /** «Editează geometria macro»: vizualizatorul structurii deschide editorul */
  laEditeaza?: () => void;
  /** mouse-ul a ajuns pe «Editează»: deschiderea editorului se poate cere */
  laPregatesteEditarea?: () => void;
  /** ce mai pune disciplina sub acțiuni (butonul de editare a nivelului) */
  extra?: ReactNode;
  /** Acțiunile VIZUALIZATORULUI în «⋯» al unei arhitecturi, după «Refă
   *  excavația» — azi «Recalculează căile de evacuare» (Oana, 22.10.2026).
   *  Vin de acolo fiindcă au nevoie de starea lui (editarea nesalvată,
   *  recitirea documentului); `inchide` strânge meniul. */
  meniuArhitectura?: (inchide: () => void) => ReactNode;
  /** Vizualizatorul arată un nivel de subsol: variantele căilor de evacuare (cu /
   *  fără scări) privesc doar subsolul, deci numai acolo apare comutatorul lor. */
  laSubsol?: boolean;
}) {
  const t = useT();
  const { registru, projectId } = useRegistru();
  const { potScrie } = useRolProiect();
  const actiuni = useActiuni();
  const [comutator, setComutator] = useState<DOMRect | null>(null);
  const [maiMult, setMaiMult] = useState(false);
  if (!registru) return null;

  const dupaId = (id: string | null | undefined) =>
    (id ? registru.solutii.find((s) => s.id === id) : undefined) ?? null;
  const el = rand.discipline === "structure"
    ? elementulRandului(registru.solutii, rand.id) : null;
  const nume = el ? numeElement(el) : numeRand(rand);
  const faze = el ? fazeleElementului(el) : fazeleRandului(rand);
  const amonte = rand.discipline === "structure" ? dupaId(rand.lineage.architecture)
    : rand.discipline === "architecture" ? dupaId(rand.lineage.site_plan) : null;
  const sub = [
    amonte ? t("nav.pe", { solutie: numeRand(amonte) }) : null,
    rand.discipline === "structure" && rand.faza !== "schita" ? numeFaza(rand.faza) : null,
    corp ? (corp === CORP_TOATE ? t("viz.toate_corpurile") : t("bloc.corp", { cod: corp })) : null,
  ].filter(Boolean).join(" · ");
  const cand = candRand(el?.fel === "solutie" ? el.macro : rand);
  const ocupat = !!actiuni.ocupat;
  const editare = rand.structura?.editare ?? null;
  const editataDin = editare ? dupaId(editare.din) : null;
  const editataDinEl = editataDin ? elementulRandului(registru.solutii, editataDin.id) : null;

  // --- structura ----------------------------------------------------------
  const macro = el?.fel === "solutie" ? el.macro : null;
  const poateDetaliat = poatePornireDetaliu(el);
  const arh = rand.lineage.architecture ?? null;
  // și pe o schiță care încă rulează: cererea se ține pe ea (contract §5.1)
  const poateGenera = el?.fel === "schita" && poateGeneraSolutia(registru.solutii, arh);
  // «Importă structura» stă lângă «Generează»: pe aceeași arhitectură încap
  // una generată și una importată din planșe; fără piese desenate rămâne
  // gri și spune de ce
  const poateImporta = el?.fel === "schita"
    && poateGeneraSolutia(registru.solutii, arh, "structurist");
  const areDesenata = areStructuraDesenata(registru.solutii, arh);
  const eSchita = el?.fel === "schita";
  const poateEdita = rand.discipline === "structure" && rand.status === "ready" && !!laEditeaza;
  const nesincronizata = rand.structura?.arhitectura_editata_la ?? null;
  const poateRerula = !!macro && macro.sursa !== "om" && !eDeschis(macro.stare)
    && rand.faza === "macro";

  const butoaneStructura = rand.discipline === "structure" && potScrie && (
    <>
      {poateDetaliat && macro && (
        <button type="button" className="btn btnp" disabled={ocupat}
                onClick={() => actiuni.pornesteDetaliat(macro)}>
          {t("actiune.porneste_detaliat")}
        </button>
      )}
      {poateGenera && arh && (
        <button type="button" className="btn btnp" disabled={ocupat}
                onClick={() => actiuni.genereazaSolutia(arh)}>
          {t("actiune.genereaza")}
        </button>
      )}
      {poateImporta && arh && (
        <button type="button" className="btn" disabled={ocupat || !areDesenata}
                title={t(areDesenata ? "bloc.importa_titlu" : "bloc.import_fara_structura")}
                onClick={() => actiuni.importaStructura(arh)}>
          {t("actiune.importa")}
        </button>
      )}
      {poateEdita && (
        <button type="button" className="btn" disabled={ocupat} onClick={laEditeaza}
                onPointerEnter={laPregatesteEditarea} onFocus={laPregatesteEditarea}
                title={t("bloc.editeaza_titlu")}>
          {t("actiune.editeaza_macro")}
        </button>
      )}
      {/* pe o SCHIȚĂ, acțiunea soluției rămâne la vedere, gri: omul vede că
          există și, din titlu, ce trebuie făcut întâi */}
      {eSchita && (
        <button type="button" className="btn" disabled
                title={t("bloc.schita_fara_detaliu")}>
          {t("actiune.porneste_detaliat")}
        </button>
      )}
    </>
  );

  const maiMultStructura = rand.discipline === "structure" && potScrie && (
    <>
      {poateRerula && macro && (
        <button type="button" role="menuitem" className="meniu-rand" disabled={ocupat}
                onClick={() => { setMaiMult(false); void actiuni.reruleaza(macro); }}>
          {t("actiune.reruleaza")}
        </button>
      )}
      {eSchita && (
        <button type="button" role="menuitem" className="meniu-rand" disabled
                title={t("bloc.schita_fara_rerulare")}>
          {t("actiune.reruleaza")}
        </button>
      )}
      {arh && (
        <button type="button" role="menuitem" className="meniu-rand" disabled={ocupat}
                onClick={() => { setMaiMult(false); void actiuni.reiaSchita(arh); }}>
          {t("actiune.reia_schita")}
        </button>
      )}
      {el?.fel === "solutie" && (
        <button type="button" role="menuitem" className="meniu-rand pericol" disabled={ocupat}
                onClick={() => { setMaiMult(false);
                                 void actiuni.sterge(el.macro,
                                   caleEtapa(projectId, "structure", null, arh)); }}>
          {t("actiune.sterge_solutia")}
        </button>
      )}
      {eSchita && (
        <button type="button" role="menuitem" className="meniu-rand pericol" disabled
                title={t("bloc.schita_fara_stergere")}>
          {t("actiune.sterge_solutia")}
        </button>
      )}
    </>
  );

  // «Așezarea pe module»: ecranul citește raportul din documentul PLANULUI
  // (`diagnostics.asezare`, 14.10.2026); de pe arhitectură se ajunge la planul ei
  const planAsezarii = rand.discipline === "site_plan" ? rand.id : (rand.lineage.site_plan ?? null);
  const maiMultArhitectura = rand.discipline === "architecture" && (
    <>
      {planAsezarii && (
        <Link role="menuitem" className="meniu-rand" to={`/p/${projectId}/excavatie/${planAsezarii}`}
              title={t("bloc.excavatia_titlu")}>
          {t("bloc.excavatia")}
        </Link>
      )}
      {potScrie && poateRefaceSubsolul(rand) && (
        <button type="button" role="menuitem" className="meniu-rand" disabled={ocupat}
                title={t("bloc.subsol_titlu")}
                onClick={() => { setMaiMult(false);
                                 void actiuni.pornesteJob(`subsol:${rand.id}`,
                                   "basement_architecture",
                                   { upstream_solution_id: rand.id }); }}>
          {t("bloc.refa_subsolul")}
        </button>
      )}
      {potScrie && planAsezarii && dupaId(planAsezarii) && (
        <button type="button" role="menuitem" className="meniu-rand" disabled={ocupat}
                title={t("bloc.excavatia_refa_titlu")}
                onClick={() => { setMaiMult(false); void actiuni.refaExcavatia(dupaId(planAsezarii)!); }}>
          {t("bloc.refa_excavatia")}
        </button>
      )}
      {meniuArhitectura?.(() => setMaiMult(false))}
    </>
  );
  const maiMultSituatie = rand.discipline === "site_plan" && (
    <>
      <Link role="menuitem" className="meniu-rand" to={`/p/${projectId}/excavatie/${rand.id}`}
            title={t("bloc.excavatia_titlu")}>
        {t("bloc.excavatia")}
      </Link>
      {/* exportul PUD (27.10.2026): planșa U3 din planul ăsta, după ce agentul
          i-a desenat circulația */}
      {areCirculatia(rand) && (
        <Link role="menuitem" className="meniu-rand" to={`/p/${projectId}/pud/${rand.id}`}
              title={t("bloc.pud_titlu")}>
          {t("bloc.pud")}
        </Link>
      )}
      {/* «Refă planul cu agentul» (31.10.2026, din fostul panou al agentului):
          agentul rescrie circulația, locurile, trotuarele și zonele planului de la
          terenul gol; cât rulează deja pe planul ăsta, nu se mai pornește unul */}
      {potScrie && (
        <button type="button" role="menuitem" className="meniu-rand"
                disabled={ocupat || rand.rulare?.kind === "site_plan_agent"}
                title={t("agent_plan.refa_expl")}
                onClick={() => { setMaiMult(false);
                                 if (!window.confirm(t("agent_plan.refa_confirma"))) return;
                                 void actiuni.pornesteJob(`agent:${rand.id}`, "site_plan_agent",
                                                          { upstream_solution_id: rand.id }); }}>
          {t("agent_plan.refa")}
        </button>
      )}
      {potScrie && (
        <button type="button" role="menuitem" className="meniu-rand" disabled={ocupat}
                title={t("bloc.excavatia_refa_titlu")}
                onClick={() => { setMaiMult(false); void actiuni.refaExcavatia(rand); }}>
          {t("bloc.refa_excavatia")}
        </button>
      )}
    </>
  );
  const areMaiMult = rand.discipline === "architecture"
    || (rand.discipline === "structure" && potScrie)
    || !!maiMultSituatie;

  return (
    <div className="bloc-solutie">
      <span className="bloc-cap">
        <span className="bloc-nume">{nume}</span>
        {rand.promoted && <span className="nav-chip acc">★ {t("bloc.favorita")}</span>}
        <button type="button" className="bloc-schimba" aria-haspopup="dialog"
                aria-expanded={!!comutator}
                onClick={(e) => setComutator(comutator ? null
                  : e.currentTarget.getBoundingClientRect())}>
          {t("bloc.schimba")}
        </button>
      </span>
      {sub && <span className="bloc-sub">{sub}</span>}
      {cand && <span className="bloc-cand" title={cand.titlu}>{cand.text}</span>}
      {rand.discipline === "architecture" && laSubsol && (
        <VarianteEvacuare rand={rand} solutii={registru.solutii} projectId={projectId} />
      )}
      {editare && (
        <span className="nav-chip met" title={t("bloc.editata_titlu")}>
          {editataDinEl
            ? t("bloc.editata_din", { solutie: numeElement(editataDinEl), n: editare.blocate })
            : t("bloc.editata_blocate", { n: editare.blocate })}
        </span>
      )}
      {rand.discipline === "structure" && rand.sursa === "structurist" && (
        <span className="nav-chip met" title={t("bloc.importata_titlu")}>
          {t("bloc.importata")}
        </span>
      )}
      {rand.discipline === "architecture" && rand.summary.editat?.la && (
        <span className="nav-chip mut">
          {t("bloc.arhitectura_editata", { la: dataOra(rand.summary.editat.la) })}
        </span>
      )}
      {nesincronizata && (
        <span className="nav-chip amb" title={t("bloc.nesincronizata_titlu")}>
          ⚠ {t("bloc.nesincronizata")}
        </span>
      )}
      {/* CIRCULAȚIA VECHE: arhitectura ★ are alte scări de evacuare la teren decât
          cele după care s-a trasat planul (Oana, 24.10.2026). Doar semnal: o
          aduce la zi «Refă planul cu agentul», din panoul agentului; nimic nu
          pornește singur. */}
      {rand.discipline === "site_plan" && rand.summary.circulatie_veche && (
        <span className="nav-chip amb bloc-circulatie-veche" title={t("bloc.circulatie_veche_titlu")}>
          ⚠ {t(rand.summary.circulatie_veche.scari ? "bloc.circulatie_veche_cu_scari" : "bloc.circulatie_veche_fara_scari",
               { n: rand.summary.circulatie_veche.scari })}
        </span>
      )}
      {/* «⋯» PE RÂNDUL FAZELOR, în dreapta lor (Oana, 23.10.2026): pe un rând
          al lui, sub ele, lăsa un rând gol pe card. */}
      <div className="bloc-faze-rand">
        <FazeBara faze={faze} />
        {areMaiMult && (
          <span className="bloc-mai-mult">
            <button type="button" className="btn btng" aria-haspopup="menu"
                    aria-expanded={maiMult} onClick={() => setMaiMult((v) => !v)}
                    aria-label={t("bloc.mai_mult")}>⋯</button>
            {maiMult && (
              <MeniuLista laInchide={() => setMaiMult(false)}
                          onMouseLeave={() => setMaiMult(false)}>
                {maiMultStructura}
                {maiMultArhitectura}
                {maiMultSituatie}
              </MeniuLista>
            )}
          </span>
        )}
      </div>
      <div className="bloc-actiuni">
        {butoaneStructura}
        {rand.discipline !== "structure" && !rand.promoted && rand.status === "ready"
          && potScrie && (
          <button type="button" className="btn" disabled={ocupat}
                  onClick={() => actiuni.promoveaza(rand)}
                  title={t("bloc.fa_favorita_titlu")}>
            <Stea s={13} />{t("bloc.fa_favorita")}
          </button>
        )}
        {rand.rulare && eDeschis(rand.stare) && potScrie && rand.discipline === "structure" && (
          <button type="button" className="btn" disabled={ocupat || rand.rulare.oprire_ceruta}
                  onClick={() => actiuni.opreste(rand)}>
            {rand.rulare.oprire_ceruta ? t("faza.se_opreste") : t("actiune.opreste")}
          </button>
        )}
        {nesincronizata && arh && potScrie && (
          <button type="button" className="btn" disabled={ocupat}
                  onClick={() => actiuni.reiaSchita(arh)}>
            {t("actiune.reia_schita")}
          </button>
        )}
        {extra}
      </div>
      {actiuni.eroare && <span className="bloc-eroare">{actiuni.eroare}</span>}
      {comutator && (
        <Comutator disciplina={rand.discipline} ancora={comutator}
                   laInchide={() => setComutator(null)} />
      )}
    </div>
  );
}

/** Ce spune un ecran când registrul n-a putut fi citit deloc: fără el nu se
 *  știe ce soluții are proiectul, deci nu se desenează nimic în locul lor —
 *  dar nici nu se învârte «Se încarcă…» la nesfârșit. */
export function EroareRegistru({ motiv }: { motiv: string }) {
  const t = useT();
  const { reciteste } = useRegistru();
  return (
    <div className="card nav-eroare" role="alert">
      <b>{t("nav.eroare.registru")}</b>
      <p className="note">{t("nav.eroare.registru_text", { motiv })}</p>
      <button type="button" className="btn" onClick={() => { void reciteste(); }}>
        {t("nav.eroare.reincearca")}
      </button>
    </div>
  );
}

/** Ce spune vizualizatorul când id-ul din cale nu e în registru, sau e al
 *  altei discipline: eroarea, cu drum spre soluția implicită (Î24). */
export function EroareSolutie({ fel, href }: {
  fel: "necunoscuta" | "alta_disciplina" | "corp_necunoscut";
  href: string | null;
}) {
  const t = useT();
  return (
    <div className="card nav-eroare" role="alert">
      <b>{t(`nav.eroare.${fel}` as const)}</b>
      <p className="note">{t(`nav.eroare.${fel}_text` as const)}</p>
      {href && <Link className="btn" to={href}>{t("nav.eroare.implicita")}</Link>}
    </div>
  );
}
